import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
import { usePromotion } from '@/helpers/composables/usePromotion';

import { OfferLocation } from '@/helpers/offer-helper';
import { SraPlacement, type Offer } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { getUserLocationCountryCode } from '@/helpers/geo-location-helper';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { freeTrialSwiperRules } from '@/helpers/promotion-helper';
import { useSingleQTV } from '@/features/buybox/composables/useSingleQTV';

import { useExperiment } from '@/libraries/experiment';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { qtvProviders, qtvProviderType } from '@/features/buybox/helpers/quality-tv-data';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { useDoubleStackExp } from '@/components/experiments/DoubleStackExp';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import SponsoredRecommendation from '@/features/sponsored-recommendations/components/SponsoredRecommendation.vue';
import {
	useAdServer,
	useGetAdServerPlacement,
	attachAdServerContexts,
} from '@/features/ad-server/composables/useAdServer';
import adminConfig from '@/features/promotions/config.json';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';
import { useNativeAds } from '@/features/native-ads/composables/useNativeAds';
import ListPreRollVideoAd from '@/components/experiments/UnderPerformingUrlsExp/ListPreRollVideoAd.vue';
import { useAppleBingeExp } from '@/components/experiments/AppleBingeExp';

interface PromotionItem {
	id: string;
	type: 'sqtv' | 'qtv' | 'sr' | 'ft' | 'nativeSqtv' | 'preRoll';
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PromotionPlacement',
  props: {
    offers: null,
    showFreeTrial: { type: Boolean },
    isFreeBuyBox: { type: Boolean },
    title: null,
    additionalContexts: null,
    trackingContexts: null,
    seasonNumber: null,
    episodeNumber: null,
    rowIndex: null
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: TitleOfferFragment | null;
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offers: Offer[];
	showFreeTrial: boolean;
	isFreeBuyBox: boolean;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	trackingContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	rowIndex?: number;
};

const AdServerPromotionPlacement = () => import('@/features/ad-server/components/AdServerPromotionPlacement.vue');
const ListPromotionRow = () => import('@/features/buybox/components/list/ListPromotionRow.vue');
const QualityTV = () => import('@/features/buybox/components/list/QualityTV.vue');
const SingleQTV = () => import('@/features/buybox/components/list/SingleQTV.vue');
const FreeTrialSwiperOS = () => import('@/features/buybox/components/grid/FreeTrialSwiperOS.vue');
const NativeSQTV = () => import('@/features/native-ads/components/NativeAdSQTVList.vue');
const PromotionSwiper = () => import('@/features/buybox/components/list/PromotionSwiper.vue');



const { isProUser } = storeToRefs(useUserStore());
const { country } = storeToRefs(useLanguageStore());
const { pagesVisited } = storeToRefs(useTrackingStore());



const { promotionOffer, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => props.offers,
});

const { activeSlots: nativeAdSlots } = useNativeAds({ title: () => props.title });
const activeNativeAdSlot = computed(() => {
	if (nativeAdSlots.value.includes('SQTV_LIST_DYNAMIC')) return 'sqtv-list';
	return null;
});

const { isOverProviderTitle } = useOverProviderExp({ promotionOffer });

const { activeVariantWithControlGroup: buyRentExpVariant } = useExperiment('EXP_MERGE_BUY_RENT');

// Apple Binge Exp
const { activeVariantWithControlGroup: appleBingeExpVariant } = useExperiment('EXP_APPLE_BINGE_2');
const { appleBingeExpShortName } = useAppleBingeExp();
// Apple Binge Exp

// ** SQTV SR **

const { sponsoredAd, isShadowCampaign, hasActiveSRCampaign } = useSendBidId({
	pageType: 'VIEW_BUYBOX',
	placement: SraPlacement.Buybox,
	jwEntityID: props.title.id,
	isSeo: pagesVisited.value <= 1,
	buyBoxType: NewBuyBoxView.LIST,
});

const seoCampaignConfig = computed(() => (adminConfig?.sr_sqtv_autoswipe?.seo ?? {}) as Record<string, number>);
const clientCampaignConfig = computed(() => (adminConfig?.sr_sqtv_autoswipe?.client ?? {}) as Record<string, number>);

const isSeoCampaignFromConfig = computed(() => {
	const campaignName = sponsoredAd.value?.campaign?.name;

	return Object.keys(seoCampaignConfig.value).includes(campaignName || '');
});

const isClientCampaignFromConfig = computed(() => {
	const campaignName = sponsoredAd.value?.campaign?.name;

	return Object.keys(clientCampaignConfig.value).includes(campaignName || '');
});

const autoSwipeDelay = computed(() => {
	if (activeNativeAdSlot.value) return 1500;

	if (!hasActiveSRCampaign.value) return undefined;

	const campaignName = sponsoredAd.value?.campaign?.name;

	if (isSeoCampaignFromConfig.value && campaignName) {
		return seoCampaignConfig.value[campaignName];
	}
	if (isClientCampaignFromConfig.value && campaignName) {
		return clientCampaignConfig.value[campaignName];
	}

	// SEO campaign should take 1s before auto swiping
	// campaign delivery estimates showed that the Client part alone is enough
	if (pagesVisited.value <= 1) return 1 * 1000;

	// no delay for the Client campaign
	return 1 * 1000;
});

const qtvAdditionalContexts = computed(() => props.additionalContexts.concat(props.trackingContexts));

const autoSwipeSrCampaign = computed(() => {
	if (activeNativeAdSlot.value) return true;
	const userCountry = getUserLocationCountryCode();

	// Geo targeted SR campaigns for SQTV
	const srCampaignCountries: Record<string, string> = {
		BLACK_FRIDAY_DACH_AT_SQTV_CLIENT_BURST2_SRA: 'AT',
		BLACK_FRIDAY_DACH_CH_SQTV_CLIENT_BURST2_SRA: 'CH',
	};
	const campaignName = sponsoredAd.value?.campaign?.name ?? '';

	// App Only Campaigns i.e. /de would get a campaign returned even outside geo targeting
	const alwaysAutoSwipeAppOnlyCampaigns = [
		'BLACK_FRIDAY_DACH_AT_SQTV_SEO_BURST2_SRA',
		'BLACK_FRIDAY_DACH_CH_SQTV_SEO_BURST2_SRA',
	];

	if (alwaysAutoSwipeAppOnlyCampaigns.includes(campaignName)) return true;

	// keeping these separate in case they require different handling in future
	if (isSeoCampaignFromConfig.value) return true;
	if (isClientCampaignFromConfig.value) return true;

	const currentCampaignCountry = srCampaignCountries[campaignName];

	if (!campaignName || !userCountry) return false;

	return currentCampaignCountry === userCountry;
});

// Double Stack Experiment
const { activeVariantWithControlGroup: doubleStackExpVariant } = useExperiment('DOUBLE_STACK_EXP_3');

const { hardcodedShortNameDSExp } = useDoubleStackExp();
// Double Stack Experiment

// UNDERPERFORMING URLS EXPERIMENT
const { activeVariantWithControlGroup: underperformingUrlsExpVariant } = useExperiment('EXP_UNDERPERFORMING_URLS');
// UNDERPERFORMING URLS EXPERIMENT

const hardcodedQTVShortName = computed(() => {
	if (underperformingUrlsExpVariant.value) return 'amp';
	return null;
});

const hardcodedSQTVShortName = computed(() => {
	if (underperformingUrlsExpVariant.value) return 'amp';
	// return catalogue short name here when no override is provided, to pass to isSQTVEmpty check

	return catalogueItem.value?.shortName || null;
});

// SQTV active logic
const isSingleQTVActive = computed(() => {
	if (isProUser.value) return false;

	if (underperformingUrlsExpVariant.value) return underperformingUrlsExpVariant.value !== 'control';
	if (buyRentExpVariant.value) return false;

	return catalogueItem.value?.placement === 'sqtv';
});

// Now that we have final isSingleQTVActive and hardcodedSQTVShortName, we can use them
const { isSQTVEmpty, loading: isSQTVLoading } = useSingleQTV(
	() => props.title,
	hardcodedSQTVShortName,
	isSingleQTVActive
);

const showSQTV = computed(() => {
	if (doubleStackExpVariant.value) return false;
	if (underperformingUrlsExpVariant.value) return underperformingUrlsExpVariant.value !== 'control';

	return isSingleQTVActive.value && !isSQTVEmpty.value;
});

const sqtvFailedToRender = computed(() => isSingleQTVActive.value && isSQTVEmpty.value);

const freeTrialActive = computed(() => {
	if (isProUser.value) return false;
	else if (activeNativeAdSlot.value === 'sqtv-list') return false;
	if (underperformingUrlsExpVariant.value) return underperformingUrlsExpVariant.value === 'control';
	if (buyRentExpVariant.value) return true;
	if (appleBingeExpVariant.value) return true;

	if (sqtvFailedToRender.value) return true;

	return catalogueItem.value?.placement === 'ft' || (!catalogueItem.value?.placement && props.showFreeTrial);
});

/* Override the config provider shortName  */
const hardcodedFTShortName = computed(() => {
	if (underperformingUrlsExpVariant.value) return 'amp';
	if (hardcodedShortNameDSExp.value) return hardcodedShortNameDSExp.value;

	// Apple Binge Exp
	if (appleBingeExpShortName.value) return appleBingeExpShortName.value;
	// Apple Binge Exp
	return null;
});

const hideTVODQTV = computed(
	() =>
		catalogueItem.value?.placement === 'qtv' &&
		!['FREE', 'JWTV'].includes(qtvProviderType[catalogueItem.value.shortName]) &&
		props.isFreeBuyBox
);

const qualityTvActive = computed(() => {
	if (doubleStackExpVariant.value || buyRentExpVariant.value || appleBingeExpVariant.value) return false;

	return (
		!isProUser.value &&
		// get qtv placement from config
		catalogueItem.value?.placement === 'qtv' &&
		qtvProviders.includes(catalogueItem.value.shortName) &&
		// Itunes TVOD QTV is not a Free Trial, cannot be shown when filter is Free.
		!hideTVODQTV.value
	);
});

const freeTrialSwiperActive = computed(() => {
	if (isProUser.value) return false;
	if (!freeTrialSwiperRules) return false;
	// Experiments
	if (
		isOverProviderTitle.value ||
		doubleStackExpVariant.value ||
		buyRentExpVariant.value ||
		appleBingeExpVariant.value
	)
		return false;

	// ATV FreeTrials take precedence
	if (catalogueItem.value?.shortName === 'atp') return false;

	return freeTrialSwiperRules[country.value] != null;
});

/*
 * Ad Server Mutation
 * Calculate what placement would be shown and send to ad server
 * This informs the ad server about our intent for ALL countries
 */
const adServerPlacement = computed(() => {
	if (showSQTV.value) return 'sqtv';
	if (qualityTvActive.value) return 'qtv';
	if (freeTrialSwiperActive.value) return 'swiper';
	if (freeTrialActive.value) return 'ft';

	return null;
});

const adServerProviders = computed(() =>
	adServerPlacement.value === 'swiper' ? freeTrialSwiperRules[country.value] : [catalogueItem.value?.shortName]
);
useGetAdServerPlacement({
	placement: adServerPlacement,
	providers: adServerProviders,
	title: () => props.title,
	enabled: () => !!props.title && props.episodeNumber == null,
});

const { adServerStatus, adServerCreative } = useAdServer(() => props.title.id);

const isValidAdServerPromotion = computed(() => adServerPlacement.value === 'sqtv' && adServerCreative.value != null);

/*
 * Short-circuit Ad Server Promotion Rendering
 * Conditions where we do not want to render Ad Server Promotions even if it is an active country and has a valid promotion
 * 1. Sponsored Recommendations - to avoid conflicts with SR campaigns
 * 2. Experiment - until we have full coordination between Ad Server and Experiments
 */
const overrideAdServerPlacement = computed(() => {
	if (sponsoredAd.value?.campaign) return true;
	return false;
});

const adServerFlexContextValue = computed<'override' | 'disabled'>(() => {
	if (overrideAdServerPlacement.value && adServerStatus.value === 'loaded') return 'override';
	return 'disabled';
});

const renderAdServerPromotion = computed(() => {
	if (isProUser.value) return false;
	if (overrideAdServerPlacement.value) return false;
	if (adServerStatus.value === 'disabled' || adServerStatus.value === 'failed') return false;
	return isValidAdServerPromotion.value;
});

const renderSRWithoutSwiper = computed(() => {
	return (
		sponsoredAd.value?.campaign &&
		adminConfig.sr_sqtv_autoswipe.no_swiper?.includes(sponsoredAd.value.campaign.name)
	);
});

/*
 * Promotion Items for Swiper
 * Collect all active promotions to display as slides
 */
const promotionItems = computed((): PromotionItem[] => {
	const items: PromotionItem[] = [];

	// UNERPERFORMING URLS EXPERIMENT
	if (underperformingUrlsExpVariant.value === 'variant_2') items.push({ id: 'preRoll', type: 'preRoll' });
	// UNERPERFORMING URLS EXPERIMENT

	// Add promotions in priority order (based on original v-else-if chain)
	if (showSQTV.value) {
		items.push({ id: 'sqtv', type: 'sqtv' });
	}
	if (qualityTvActive.value) {
		items.push({ id: 'qtv', type: 'qtv' });
	}
	if (freeTrialActive.value) {
		items.push({ id: 'ft', type: 'ft' });
	}

	if (sponsoredAd.value?.campaign && !buyRentExpVariant.value && !underperformingUrlsExpVariant.value) {
		if (renderSRWithoutSwiper.value) return [{ id: 'sr', type: 'sr' }];

		items.push({ id: 'sr', type: 'sr' });
	}

	if (activeNativeAdSlot.value === 'sqtv-list') {
		items.push({ id: 'nativeSqtv', type: 'nativeSqtv' });
	}

	return items;
});
const hideSwiperControls = computed(() => {
	// Hide swiper controls for Pluribus SQTV SR campaign in US
	if (
		(sponsoredAd.value?.campaign?.name === 'PLURIBUS_CLIENT-USER_US_BURST2_SRA' ||
			sponsoredAd.value?.campaign?.name === 'PLURIBUS_US_SEO-USER_BURST2_SEO_SRA') &&
		country.value === 'US'
	) {
		return true;
	}
	return false;
});

function onClickOut(payload: {
	buyboxOffer: TitleOfferFragment | null;
	offerLocation: OfferLocation.FREE_TRIAL;
	row: HTMLElement | null;
}) {
	emit('clickOut', {
		buyboxOffer: payload.buyboxOffer,
		offerLocation: payload.offerLocation,
		row: payload.row,
	});
}

return { __sfc: true,AdServerPromotionPlacement, ListPromotionRow, QualityTV, SingleQTV, FreeTrialSwiperOS, NativeSQTV, PromotionSwiper, props, isProUser, country, pagesVisited, emit, promotionOffer, catalogueItem, nativeAdSlots, activeNativeAdSlot, isOverProviderTitle, buyRentExpVariant, appleBingeExpVariant, appleBingeExpShortName, sponsoredAd, isShadowCampaign, hasActiveSRCampaign, seoCampaignConfig, clientCampaignConfig, isSeoCampaignFromConfig, isClientCampaignFromConfig, autoSwipeDelay, qtvAdditionalContexts, autoSwipeSrCampaign, doubleStackExpVariant, hardcodedShortNameDSExp, underperformingUrlsExpVariant, hardcodedQTVShortName, hardcodedSQTVShortName, isSingleQTVActive, isSQTVEmpty, isSQTVLoading, showSQTV, sqtvFailedToRender, freeTrialActive, hardcodedFTShortName, hideTVODQTV, qualityTvActive, freeTrialSwiperActive, adServerPlacement, adServerProviders, adServerStatus, adServerCreative, isValidAdServerPromotion, overrideAdServerPlacement, adServerFlexContextValue, renderAdServerPromotion, renderSRWithoutSwiper, promotionItems, hideSwiperControls, onClickOut, SraPlacement, SponsoredRecommendation, attachAdServerContexts, NewBuyBoxView, ListPreRollVideoAd }
}

})